import { useLocation, useNavigate } from 'react-router'
import { keepPreviousData, useInfiniteQuery, useMutation } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { useMe } from '@shared/providers/src/MeProvider'
import { setQueriesData } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { flatten, mapCache, pageParam, QK, UserRole } from '@shared/utils'

import { NotificationType } from '@pages/Notifications/Notifications.utils'

const LIMIT = 20

export function useUserNotifications(params = {}, options = {}) {
  const me = useMe()
  const query = { ...params, limit: LIMIT }
  return useInfiniteQuery({
    queryKey: QK.users.id(me.id).notifications.list(query),
    queryFn: ({ pageParam }) => API.users.id(me.id).notifications.list({ ...query, offset: pageParam * LIMIT }),
    placeholderData: keepPreviousData,
    select: flatten,
    initialPageParam: 0,
    getNextPageParam: pageParam(LIMIT),
    ...options,
  })
}

export function useUnreadNotificationsCount() {
  const { data, hasNextPage } = useUserNotifications({ read: false }, { enabled: false })

  const count = data ? `${data.filter((n) => !n.read).length}${hasNextPage ? '+' : ''}` : ''
  if (['0', '0+'].includes(count)) return ''
  return count
}

export function useNotificationUpdate() {
  const me = useMe()
  return useMutation({
    mutationFn: ({ id, read }) => {
      if (read) return API.users.id(me.id).notifications.id(id).mark.read()
      return API.users.id(me.id).notifications.id(id).mark.unread()
    },
    onSuccess: (notification, { id }) => {
      return setQueriesData(
        { queryKey: QK.users.id(me.id).notifications.lists },
        mapCache((item) => (item.id === id ? { ...item, ...notification } : item))
      )
    },
  })
}

export function useMarkAllRead() {
  const me = useMe()
  return useMutation({
    mutationFn: () => API.users.id(me.id).notifications.read(),
    onSuccess: () => {
      return setQueriesData(
        { queryKey: QK.users.id(me.id).notifications.lists },
        mapCache((item) => ({ ...item, read: true }))
      )
    },
  })
}

export function useNotificationRedirect() {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  return (path) => {
    const isVisitPage = pathname.startsWith('/app/visits')
    return isVisitPage ? window.open(`${import.meta.env.VITE_URL}${path}`, '_blank') : navigate(path)
  }
}

export function useNotificationOnClick(notification) {
  const me = useMe()
  const redirect = useNotificationRedirect()

  const update = useNotificationUpdate()

  return () => {
    if (!notification) return

    if (notification.read === false) update.mutate({ id: notification.id, read: true })

    if (notification.type === NotificationType.Message && notification.properties?.patientId) {
      const path = notification.properties?.messageId
        ? `/app/patients/${notification.properties.patientId}/chat?messageId=${notification.properties.messageId}`
        : `/app/patients/${notification.properties.patientId}/chat`

      return redirect(path)
    }

    if (notification.type === NotificationType.ReassignedPatient && notification.properties?.patientId) {
      return redirect(`/app/patients/${notification.properties.patientId}`)
    }

    if (notification.type === NotificationType.PatientUpdatedCBO && notification.properties?.patientId) {
      return redirect(`/app/patients/${notification.properties.patientId}`)
    }

    if (notification.type === NotificationType.DoseSpot && notification.properties?.url) {
      return window.open(notification.properties.url, '_blank')
    }

    if (notification.type === NotificationType.QuestLabQuestionnaire && notification.properties?.id) {
      return redirect(`/app/labs/${notification.properties.id}`)
    }

    if (notification.type === NotificationType.Availabilities) {
      const date = dayjs(notification.properties.startTimeTz, 'YYYY-MM-DDTHH:mm').format('YYYY-MM-DD')
      return redirect(`/app/scheduling?date=${date}`)
    }

    if (notification.type === NotificationType.NewAppointment) {
      return redirect(`/app/visits/${notification.properties.appointmentId}`)
    }

    if (notification.type === NotificationType.AppointmentScheduled) {
      const { patientId, encounterId } = notification.properties
      // ad hoc appointments have no encounter, fall back to the patient
      return redirect(encounterId ? `/app/patients/${patientId}/encounters/${encounterId}` : `/app/patients/${patientId}`)
    }

    if (notification.type === NotificationType.CompleteScheduledHours) {
      return redirect(`/app/scheduling`)
    }

    if (notification.type === NotificationType.CompletedQLinkLabTask) {
      return redirect(`/app/patients/${notification.properties.patientId}`)
    }

    if (notification.type === NotificationType.ExpiredQLinkResultTask) {
      return redirect(`/app/patients/${notification.properties.patientId}`)
    }

    if (notification.type === NotificationType.TaskCreation) {
      const taskIdentifier = notification.properties.taskIdentifier
      return redirect(me.role === UserRole.Admin ? `/app/kanban?task=${taskIdentifier}` : `/app?task=${taskIdentifier}`)
    }

    if (
      notification.type === NotificationType.EscalationTaskCreated ||
      notification.type === NotificationType.EscalationTaskReassigned ||
      notification.type === NotificationType.EscalationTaskCompleted
    ) {
      const taskId = notification.properties.taskId
      return redirect(me.role === UserRole.Admin ? `/app/kanban?task=${taskId}` : `/app/escalations?escalationsTab=done&task=${taskId}`)
    }

    if ([NotificationType.LicenseExpiration, NotificationType.CertificationExpiration].includes(notification.type)) {
      const taskId = notification.properties?.taskId
      return redirect(taskId ? `/app?tab=credentials&credential=${taskId}` : `/app?tab=credentials`)
    }
  }
}
